import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import { toast } from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getImageUrl } from '../../utils/imageUtils';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    EyeIcon,
    XMarkIcon,
    PhotoIcon,
} from '@heroicons/react/24/outline';

interface Project {
    _id: string;
    title: string;
    slug: string;
    subtitle?: string;
    category: string;
    shortDescription: string;
    fullDescription: string;
    logo?: string;
    banner?: string;
    image?: string;
    url?: string;
    color: string;
    objectives?: string[];
    highlights?: string[];
    results?: string[];
    overview?: string;
    isActive: boolean;
    order: number;
}

const ProjectsManagement: React.FC = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        subtitle: '',
        category: '',
        shortDescription: '',
        fullDescription: '',
        logo: '',
        banner: '',
        image: '',
        url: '',
        color: 'blue',
        objectives: [] as string[],
        highlights: [] as string[],
        results: [] as string[],
        overview: '',
        isActive: true,
        order: 0,
    });
    const [newObjective, setNewObjective] = useState('');
    const [newHighlight, setNewHighlight] = useState('');
    const [newResult, setNewResult] = useState('');

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/projects');
            setProjects(response.data.data || []);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch projects');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (project?: Project) => {
        if (project) {
            setSelectedProject(project);
            setIsEditMode(true);
            setFormData({
                title: project.title,
                slug: project.slug,
                subtitle: project.subtitle || '',
                category: project.category,
                shortDescription: project.shortDescription,
                fullDescription: project.fullDescription,
                logo: project.logo || '',
                banner: project.banner || '',
                image: project.image || '',
                url: project.url || '',
                color: project.color,
                objectives: project.objectives || [],
                highlights: project.highlights || [],
                results: project.results || [],
                overview: project.overview || '',
                isActive: project.isActive,
                order: project.order,
            });
        } else {
            setIsEditMode(false);
            setSelectedProject(null);
            setFormData({
                title: '',
                slug: '',
                subtitle: '',
                category: '',
                shortDescription: '',
                fullDescription: '',
                logo: '',
                banner: '',
                image: '',
                url: '',
                color: 'blue',
                objectives: [],
                highlights: [],
                results: [],
                overview: '',
                isActive: true,
                order: 0,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedProject(null);
        setIsEditMode(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Auto-generate slug from title
        if (name === 'title' && !isEditMode) {
            const slug = value
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
            setFormData(prev => ({ ...prev, slug }));
        }
    };

    const handleFileUpload = async (file: File, type: 'logo' | 'banner' | 'image') => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder', 'projects');

            const response = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Use fullUrl if available, otherwise construct from url
            const fileUrl = response.data.fullUrl || (response.data.url?.startsWith('http')
                ? response.data.url
                : `https://api.crm.we4task.com${response.data.url}`);

            setFormData(prev => ({ ...prev, [type]: fileUrl }));
            toast.success('File uploaded successfully');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to upload file');
        }
    };

    const handleAddObjective = () => {
        if (newObjective.trim()) {
            setFormData(prev => ({
                ...prev,
                objectives: [...prev.objectives, newObjective.trim()]
            }));
            setNewObjective('');
        }
    };

    const handleRemoveObjective = (index: number) => {
        setFormData(prev => ({
            ...prev,
            objectives: prev.objectives.filter((_, i) => i !== index)
        }));
    };

    const handleAddHighlight = () => {
        if (newHighlight.trim()) {
            setFormData(prev => ({
                ...prev,
                highlights: [...prev.highlights, newHighlight.trim()]
            }));
            setNewHighlight('');
        }
    };

    const handleRemoveHighlight = (index: number) => {
        setFormData(prev => ({
            ...prev,
            highlights: prev.highlights.filter((_, i) => i !== index)
        }));
    };

    const handleAddResult = () => {
        if (newResult.trim()) {
            setFormData(prev => ({
                ...prev,
                results: [...prev.results, newResult.trim()]
            }));
            setNewResult('');
        }
    };

    const handleRemoveResult = (index: number) => {
        setFormData(prev => ({
            ...prev,
            results: prev.results.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (isEditMode && selectedProject) {
                await api.put(`/admin/projects/${selectedProject._id}`, formData);
                toast.success('Project updated successfully');
            } else {
                await api.post('/admin/projects', formData);
                toast.success('Project created successfully');
            }
            handleCloseModal();
            fetchProjects();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to save project');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this project?')) return;

        try {
            await api.delete(`/admin/projects/${id}`);
            toast.success('Project deleted successfully');
            fetchProjects();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete project');
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            ['link', 'image'],
            ['clean']
        ],
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="spinner"></div>
                    <p className="mt-4 text-gray-600">Loading projects...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Projects Management</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Add Project
                </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {projects.map((project) => (
                            <tr key={project._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{project.title}</div>
                                    <div className="text-sm text-gray-500">{project.slug}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {project.category}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${project.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {project.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {project.order}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={() => navigate(`/projects/${project.slug}`)}
                                            className="text-blue-600 hover:text-blue-900"
                                        >
                                            <EyeIcon className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleOpenModal(project)}
                                            className="text-indigo-600 hover:text-indigo-900"
                                        >
                                            <PencilIcon className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(project._id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                            <form onSubmit={handleSubmit} className="bg-white">
                                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {isEditMode ? 'Edit Project' : 'Add New Project'}
                                    </h3>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                                            <input
                                                type="text"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                                            <input
                                                type="text"
                                                name="slug"
                                                value={formData.slug}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                                            <input
                                                type="text"
                                                name="subtitle"
                                                value={formData.subtitle}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                                            <input
                                                type="text"
                                                name="category"
                                                value={formData.category}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Short Description *</label>
                                        <textarea
                                            name="shortDescription"
                                            value={formData.shortDescription}
                                            onChange={handleInputChange}
                                            required
                                            rows={3}
                                            maxLength={500}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">{formData.shortDescription.length}/500</p>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Description *</label>
                                        <ReactQuill
                                            theme="snow"
                                            value={formData.fullDescription}
                                            onChange={(value) => setFormData(prev => ({ ...prev, fullDescription: value }))}
                                            modules={quillModules}
                                            className="bg-white"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Banner</label>
                                            <p className="text-xs text-gray-500 mb-2">📐 Recommended: 1920x600px (Full-width banner)</p>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    name="banner"
                                                    value={formData.banner}
                                                    onChange={handleInputChange}
                                                    placeholder="Banner URL"
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                                <label className="px-3 py-2 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200">
                                                    <PhotoIcon className="w-5 h-5" />
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'banner')}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
                                            <p className="text-xs text-gray-500 mb-2">📐 Recommended: 800x600px (Project thumbnail)</p>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    name="image"
                                                    value={formData.image}
                                                    onChange={handleInputChange}
                                                    placeholder="Logo URL"
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                                <label className="px-3 py-2 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200">
                                                    <PhotoIcon className="w-5 h-5" />
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'image')}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                                            <input
                                                type="url"
                                                name="url"
                                                value={formData.url}
                                                onChange={handleInputChange}
                                                placeholder="https://example.com"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                                            <select
                                                name="color"
                                                value={formData.color}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="blue">Blue</option>
                                                <option value="orange">Orange</option>
                                                <option value="green">Green</option>
                                                <option value="purple">Purple</option>
                                                <option value="pink">Pink</option>
                                                <option value="indigo">Indigo</option>
                                                <option value="cyan">Cyan</option>
                                                <option value="violet">Violet</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Overview</label>
                                        <textarea
                                            name="overview"
                                            value={formData.overview}
                                            onChange={handleInputChange}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Objectives</label>
                                        <div className="flex space-x-2 mb-2">
                                            <input
                                                type="text"
                                                value={newObjective}
                                                onChange={(e) => setNewObjective(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddObjective())}
                                                placeholder="Add objective"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddObjective}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                            >
                                                Add
                                            </button>
                                        </div>
                                        <div className="space-y-1">
                                            {formData.objectives.map((obj, index) => (
                                                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                    <span className="text-sm">{obj}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveObjective(index)}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <XMarkIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Highlights</label>
                                        <div className="flex space-x-2 mb-2">
                                            <input
                                                type="text"
                                                value={newHighlight}
                                                onChange={(e) => setNewHighlight(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHighlight())}
                                                placeholder="Add highlight"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddHighlight}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                            >
                                                Add
                                            </button>
                                        </div>
                                        <div className="space-y-1">
                                            {formData.highlights.map((hl, index) => (
                                                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                    <span className="text-sm">{hl}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveHighlight(index)}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <XMarkIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Results</label>
                                        <div className="flex space-x-2 mb-2">
                                            <input
                                                type="text"
                                                value={newResult}
                                                onChange={(e) => setNewResult(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddResult())}
                                                placeholder="Add result"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddResult}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                            >
                                                Add
                                            </button>
                                        </div>
                                        <div className="space-y-1">
                                            {formData.results.map((res, index) => (
                                                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                    <span className="text-sm">{res}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveResult(index)}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <XMarkIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                                            <input
                                                type="number"
                                                name="order"
                                                value={formData.order}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={formData.isActive}
                                                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label className="ml-2 block text-sm text-gray-700">Active</label>
                                        </div>
                                    </div>
                                </div>

                                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        {isEditMode ? 'Update' : 'Create'} Project
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectsManagement;

